"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ResourceStashManager = void 0;
const store_1 = require("../exports/store");
const undo_manager_1 = require("./undo_manager");
const lru_cache_1 = __importDefault(require("lru-cache"));
const lodash_1 = __importDefault(require("lodash"));
const redux_batched_actions_1 = require("redux-batched-actions");
const compute_manager_1 = require("../compute_manager");
class ResourceStashManager {
    constructor(store, getRoomService) {
        this.store = store;
        this.getRoomService = getRoomService;
        this.maxSize = 5;
        this.closeClear = true;
        this.stash = new lru_cache_1.default(this.maxSize);
    }
    calcRefMap(stashResourceIds, activeDstIds) {
        const state = this.store.getState();
        const computeRefManager = new compute_manager_1.ComputeRefManager();
        for (const resourceId of stashResourceIds) {
            const currSnapshot = store_1.Selectors.getSnapshot(state, resourceId);
            const fieldMap = currSnapshot === null || currSnapshot === void 0 ? void 0 : currSnapshot.meta.fieldMap;
            if (!fieldMap) {
                continue;
            }
            computeRefManager.computeRefMap(fieldMap, resourceId, state);
        }
        const reRefMap = computeRefManager.reRefMap;
        if (reRefMap.size === 0) {
            return;
        }
        const _activeDstIds = [];
        reRefMap.forEach(linkPath => {
            const _linkPath = Array.from(linkPath);
            const linkDstAndField = _linkPath[_linkPath.length - 1];
            if (linkDstAndField == null) {
                return;
            }
            const dstId = linkDstAndField.split('-')[0];
            if (activeDstIds.includes(dstId)) {
                return;
            }
            _activeDstIds.push(dstId);
        });
        activeDstIds.push(..._activeDstIds);
        this.calcRefMap(_activeDstIds, activeDstIds);
    }
    static getIdsByResourceType(stashResourceIds) {
        const stashDatasheetIds = [];
        const stashDashboardIds = [];
        const stashFormIds = [];
        const formIdReg = /(fom\w+)/;
        const datasheetIdReg = /(dst\w+)/;
        const dashboardReg = /(dsb\w+)/;
        for (const id of stashResourceIds) {
            if (datasheetIdReg.test(id)) {
                stashDatasheetIds.push(id);
            }
            if (dashboardReg.test(id)) {
                stashDashboardIds.push(id);
            }
            if (formIdReg.test(id)) {
                stashFormIds.push(id);
            }
        }
        return {
            stashDatasheetIds,
            stashDashboardIds,
            stashFormIds
        };
    }
    analyseActiveResource() {
        const state = this.store.getState();
        const stashResourceIds = this.stash.dump().map(item => item.k);
        const roomService = this.getRoomService();
        const { stashDatasheetIds, stashDashboardIds, } = ResourceStashManager.getIdsByResourceType(stashResourceIds);
        if (stashDatasheetIds.length) {
            const activeDstIds = [...stashResourceIds];
            if (Object.keys(state.widgetMap).length) {
                const widgetDependDstIds = Object.values(state.widgetMap).reduce((ids, widget) => {
                    const id = widget.widget.snapshot.datasheetId;
                    if (id && !ids.includes(id)) {
                        ids.push(id);
                    }
                    return ids;
                }, []);
                activeDstIds.push(...widgetDependDstIds);
            }
            this.calcRefMap(stashResourceIds, activeDstIds);
            const dstIdsInRedux = store_1.Selectors.getDatasheetIds(state);
            const unActiveDstIds = lodash_1.default.difference(dstIdsInRedux, activeDstIds);
            const _batchActions = [];
            unActiveDstIds.map(id => {
                roomService.quit(id);
                _batchActions.push(store_1.StoreActions.resetDatasheet(id));
            });
            this.store.dispatch((0, redux_batched_actions_1.batchActions)(_batchActions));
        }
        if (stashDashboardIds.length) {
            const dashboardIdsInRedux = Object.keys(state.dashboardMap);
            const unActiveDashboardIds = lodash_1.default.difference(dashboardIdsInRedux, stashDashboardIds);
            const _batchActions = [];
            unActiveDashboardIds.map(id => {
                roomService.quit(id);
                _batchActions.push(store_1.StoreActions.resetDashboard(id));
            });
            this.store.dispatch((0, redux_batched_actions_1.batchActions)(_batchActions));
        }
    }
    setUndoManager(resourceId, undoManager) {
        const stashCount = this.stash.length;
        const allowClear = stashCount === this.maxSize;
        this.stash.set(resourceId, undoManager);
        if (!this.closeClear) {
            allowClear && this.analyseActiveResource();
        }
    }
    getUndoManager(resourceId) {
        let undoManager = this.stash.get(resourceId);
        if (!undoManager) {
            undoManager = new undo_manager_1.UndoManager(resourceId);
            this.setUndoManager(resourceId, undoManager);
        }
        return undoManager;
    }
    destroy() {
        this.stash.reset();
    }
}
exports.ResourceStashManager = ResourceStashManager;
//# sourceMappingURL=resource_stash_manager.js.map